import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { elementRefType } from '#/prop-types';
import DynamicWidthInput from '#/shared/components/DynamicWidthInput';
import { mergeRefs } from '#/shared/utils/react';

function TimePartInput({
  value,
  onChange,
  minValue,
  maxValue,
  valueToNumber,
  numberToValue,
  onFocus,
  onKeyDown,
  onFieldPopulated,
  required,
  placeHolder,
  inputMode,
  ariaLabel,
  ariaDescribedBy,
  inputRef,
  className,
}) {
  const [overwriteOnNextChange, setOverwriteOnNextChange] = useState(true);
  const formatValue = v => numberToValue(valueToNumber(v));

  const incrementValue = increment => {
    let nextValue;
    const numberValue = valueToNumber(value);
    const incrementPositive = increment > 0;
    const minValueNumber = valueToNumber(minValue);
    const maxValueNumber = valueToNumber(maxValue);

    if (value === '') {
      nextValue = incrementPositive ? minValueNumber : maxValueNumber;
    } else if (numberValue === maxValueNumber && incrementPositive) {
      nextValue = minValueNumber;
    } else if (numberValue === minValueNumber && !incrementPositive) {
      nextValue = maxValueNumber;
    } else {
      nextValue = valueToNumber(value) + increment;
    }

    onChange(numberToValue(nextValue));
    setOverwriteOnNextChange(true);
  };

  const validateAndUpdateValue = rawValue => {
    const nextValue = formatValue(rawValue);
    const numberValue = valueToNumber(rawValue);
    const maxValueNumber = valueToNumber(maxValue);

    if (
      Number.isInteger(numberValue) &&
      numberValue >= valueToNumber(minValue) &&
      numberValue <= maxValueNumber
    ) {
      onChange(nextValue);

      if (
        // for integer values, if any subsequent keystroke would cause the
        // max value to be exceeded indicate that the field is populated
        Number.isInteger(Number(nextValue)) &&
        valueToNumber(`${nextValue}0`) > maxValueNumber
      ) {
        onFieldPopulated();
      }
    }
  };

  const [lastValidValue, setLastValidValue] = useState(null);
  const internalInputRef = useRef();

  const internalOnKeyDown = e => {
    if (value && internalInputRef.current) {
      // new characters should always be appended to previously existing
      // text so move the cursor to the right side before each key stroke
      internalInputRef.current.setSelectionRange(value.length, value.length);
    }

    if (['Backspace', 'Delete'].includes(e.key)) {
      // clear the whole field on any backspace/delete
      e.preventDefault();
      setLastValidValue(value);
      onChange('');
    } else if (e.key === 'ArrowUp') {
      incrementValue(1);
    } else if (e.key === 'ArrowDown') {
      incrementValue(-1);
    }
  };

  return (
    <DynamicWidthInput
      type="text"
      inputMode={inputMode}
      placeholder={placeHolder}
      value={value}
      onChange={e => {
        let nextValue = e.target.value;

        if (overwriteOnNextChange) {
          nextValue = nextValue.slice(-1);
          setOverwriteOnNextChange(false);
        }

        validateAndUpdateValue(nextValue);
      }}
      onKeyDown={e => {
        internalOnKeyDown(e);
        onKeyDown(e);
      }}
      onFocus={() => {
        setOverwriteOnNextChange(true);
        onFocus();
      }}
      onBlur={() => {
        if (!value && required && lastValidValue) onChange(lastValidValue);
      }}
      ref={mergeRefs(inputRef, internalInputRef)}
      required={required}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={className}
    />
  );
}

TimePartInput.propTypes = {
  /** String displayed in the input */
  value: PropTypes.string.isRequired,
  /** Handler fired when value is changed by the input; is passed next value */
  onChange: PropTypes.func.isRequired,
  /** Minimum value allowed in the input, transformed with `valueToNumber` */
  minValue: PropTypes.string.isRequired,
  /** Maximum value allowed in the input, transformed with `valueToNumber` */
  maxValue: PropTypes.string.isRequired,
  /**
   * Function to convert display value to ordinal integer, numbers are used
   * to check that the value doesn't exceed the defined min and max and for
   * incrementing value via the up/down arrow keys
   */
  valueToNumber: PropTypes.func,
  /** Function that converts ordinal integer to display value */
  numberToValue: PropTypes.func,
  /** Handler for input's `focus` event */
  onFocus: PropTypes.func,
  /** Handler for input's `keydown` event */
  onKeyDown: PropTypes.func,
  /**
   * Fired after a written keystroke when subsequent input would cause the
   * `maxValue` to be exceeded
   */
  onFieldPopulated: PropTypes.func,
  /** If `true` the input will not be clearable once it has a valid value */
  required: PropTypes.bool,
  /** Text is displayed when input is empty */
  placeHolder: PropTypes.string,
  /** Value used for `inputMode` attribute of input element */
  inputMode: PropTypes.string,
  /** `aria-label` text for input element */
  ariaLabel: PropTypes.string,
  /** ID for element that describes the time input */
  ariaDescribedBy: PropTypes.string,
  /** `ref` object assigned to this component's input DOM element */
  inputRef: elementRefType,
  /** Pass-through for styling with styled-components */
  className: PropTypes.string,
};

TimePartInput.defaultProps = {
  valueToNumber: v => v && Number(v),
  numberToValue: n => (n != null ? String(n) : ''),
  onFocus: () => {},
  onKeyDown: () => {},
  onFieldPopulated: () => {},
  required: false,
  placeHolder: '--',
  inputMode: 'numeric',
  ariaLabel: undefined,
  ariaDescribedBy: undefined,
  inputRef: undefined,
  className: null,
};

export default TimePartInput;
